import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { isPresalePhase, getCommissionStructureText } from '../../lib/phaseUtils';
import {
  diggersPhaseLabel,
  isDiggersActivePhase,
  usesWeeklyDiggers,
  PHASE10_PHASE_ID,
  WEEKLY_DIGGERS_POOL_CONFIG,
  MONTHLY_DIGGERS_POOL_CONFIG,
  allocateClubPrizeAmounts,
  WEEKLY_DIGGERS_DISPLAY_SHARE_PRICE,
  WEEKLY_DIGGERS_DISPLAY_COMMISSION_USDT,
} from '../../lib/competitionLeaderboard.js';

// Removed all demo data creation and competition population functions
// Uses public API endpoints for security (no service role client in browser)

interface GoldDiggersClubProps {
  onGoToAuth?: (mode: 'login' | 'register') => void;
  /** Signed-in Club page. Explains entry instead of sending the member to sign-in. */
  howToEnter?: boolean;
}

const WEEKLY_PRIZE_ORDER = [1, 2, 3, '4-10', '11-20', '21-40', '41-60', '61-100'] as const;
const MONTHLY_PRIZE_ORDER = [1, 2, 3, '4-10', '11-20', '21-30', '31-50'] as const;
const PRIZE_PLACE_LABEL: Record<string, string> = {
  '1': '1st place',
  '2': '2nd place',
  '3': '3rd place',
  '4-10': '4th–10th',
  '11-20': '11th–20th',
  '21-30': '21st–30th',
  '21-40': '21st–40th',
  '31-50': '31st–50th',
  '41-60': '41st–60th',
  '61-100': '61st–100th',
};

function prizeLinesFromConfig(pcts: Record<string, number>, order: readonly (number | string)[]) {
  return order.map((key) => {
    const pct = Number(pcts[key] ?? 0);
    const each = String(key).includes('-');
    const shown = Number.isInteger(pct) ? String(pct) : String(pct);
    return {
      place: PRIZE_PLACE_LABEL[String(key)] || String(key),
      share: `${shown}%${each ? ' each' : ''}`,
    };
  });
}

function clubPurchaseDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

/** Paid referral purchases only. Bonus gifts and commission credits are not Club volume. */
function buildClubPurchaseRows(purchases: any[]) {
  return (purchases || []).flatMap((p) => {
    const amount = parseFloat(p.total_amount || 0);
    const paidShares = parseFloat(p.shares_purchased || 0);
    const rowKind = p.rowKind || 'sale';
    if (rowKind !== 'sale' || amount <= 0) return [];
    const at = new Date(p.created_at).getTime();
    return [{
      buyerLabel: `Buyer #${p.buyerIndex || '?'}`,
      amount,
      paidShares,
      countsAsSale: true,
      date: clubPurchaseDate(p.created_at),
      at,
    }];
  }).sort((a, b) => b.at - a.at);
}

/** Values under one cent keep enough digits that a real fraction is not shown as 0.00. */
function formatClubNumber(value: number, normalMaxDigits: number): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0.00';
  const abs = Math.abs(n);
  const maxDigits = abs > 0 && abs < 0.01 ? 8 : normalMaxDigits;
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: maxDigits,
  });
}

const GoldDiggersClub: React.FC<GoldDiggersClubProps> = ({ onGoToAuth, howToEnter = false }) => {

  const [allPhases, setAllPhases] = useState<any[]>([]);
  const [selectedPhase, setSelectedPhase] = useState<any>(null);
  const [competition, setCompetition] = useState<any>(null);
  const [_competitions, setCompetitions] = useState<any[]>([]);
  const [selectedCompetitionId, setSelectedCompetitionId] = useState<string | null>(null);
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [prizeTierDisplay, setPrizeTierDisplay] = useState<any[] | null>(null); // v4.1.11: DB-sourced prize tier info
  const [yourPosition, setYourPosition] = useState<any>(null); // v4.1.11: Logged-in user's rank

  const [loading, setLoading] = useState(true);
  const [_phaseCommissionsPaid, setPhaseCommissionsPaid] = useState(0); // Phase-specific commissions
  const [phaseActiveCompetitors, setPhaseActiveCompetitors] = useState(0); // Phase-specific competitor count
  const [calculatedSharesSold, setCalculatedSharesSold] = useState(0); // Shares sold including conversions
  const [phaseRevenue, setPhaseRevenue] = useState(0); // Actual cash revenue (purchases + conversions)

  // Add error state for debugging
  const [error, setError] = useState<any>(null);

  // State for expandable leaderboard details — uses opaque `ref` (e.g. 'r1') instead of user_id
  const [expandedRef, setExpandedRef] = useState<string | null>(null);
  const [showAllLeaderboard, setShowAllLeaderboard] = useState(false); // v4.1.11: Top 10 collapse toggle
  const [countdown, setCountdown] = useState<string>(''); // v4.1.11: Phase countdown timer
  const [referralDetails, setReferralDetails] = useState<any>({});
  // v4.2.0: Single Pool Top 50 state (Phase 5+)
  const [isSinglePool, setIsSinglePool] = useState(false);
  const [singlePoolPrizePool, setSinglePoolPrizePool] = useState(0);
  const [singlePoolConfig, setSinglePoolConfig] = useState<any>(null);
  const [weeks, setWeeks] = useState<any[]>([]);
  const [selectedWeek, setSelectedWeek] = useState<string | number>('auto');
  const [howToOpen, setHowToOpen] = useState(false);

  // Database access now goes through public API endpoints for security

  // Helper function to get commission structure based on phase
  const getCommissionStructure = (phase: any) => {
    const commissionText = getCommissionStructureText(phase);

    if (isPresalePhase(phase)) {
      return {
        usdt: 15,
        shares: 15,
        ...commissionText
      };
    }
    if (usesWeeklyDiggers(phase)) {
      return {
        usdt: WEEKLY_DIGGERS_DISPLAY_COMMISSION_USDT,
        shares: 0,
        description: commissionText.description,
        highlight: false,
        exclusiveText: null
      };
    }
    return {
      usdt: 15,
      shares: 0,
      description: commissionText.description,
      highlight: false,
      exclusiveText: null
    };
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Countdown uses Diggers competition end only (not investment phase end)
  useEffect(() => {
    const endDate = competition?.scoring_end || competition?.end_date;
    if (!endDate) { setCountdown(''); return; }

    const calcCountdown = () => {
      const now = new Date().getTime();
      const end = new Date(endDate).getTime();
      const diff = end - now;
      if (diff <= 0) { setCountdown('Competition Ended'); return; }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      if (days > 0) setCountdown(`${days}d ${hours}h ${minutes}m`);
      else setCountdown(`${hours}h ${minutes}m`);
    };

    calcCountdown();
    const interval = setInterval(calcCountdown, 60000);
    return () => clearInterval(interval);
  }, [competition?.scoring_end, competition?.end_date]);

  const applyLeaderboardPayload = (data: any) => {
    if (data.metrics) {
      setPhaseCommissionsPaid(data.metrics.commissions_paid || 0);
      setPhaseActiveCompetitors(data.metrics.active_competitors || 0);
      setCalculatedSharesSold(data.metrics.calculated_shares_sold || 0);
      setPhaseRevenue(data.metrics.total_revenue || 0);
    }
    if (data.competition) {
      setCompetition(data.competition);
      setSelectedCompetitionId(data.competition.id);
    }
    if (Array.isArray(data.competitions)) setCompetitions(data.competitions);
    if (Array.isArray(data.weeks)) setWeeks(data.weeks);
    if (data.week != null) setSelectedWeek(data.week);
    if (data.singlePool === true) {
      setIsSinglePool(true);
      setSinglePoolPrizePool(data.prizePool || 0);
      setSinglePoolConfig(data.config || null);
      setLeaderboard(data.leaderboard || []);
      setPrizeTierDisplay(null);
    } else {
      setIsSinglePool(false);
      setSinglePoolPrizePool(0);
      setSinglePoolConfig(null);
      setLeaderboard(data.leaderboard || []);
      setPrizeTierDisplay(data.prizeTierDisplay || null);
      setYourPosition(data.yourPosition || null);
    }
  };

  // Phase is the only UI switch. Diggers window is auto-resolved for that phase.
  const loadPhaseDiggers = async (phase: any, week: string | number = 'auto') => {
    if (!phase?.id) return;
    setSelectedPhase(phase);
    setLeaderboard([]);
    setReferralDetails({});
    setExpandedRef(null);
    setShowAllLeaderboard(false);
    setIsSinglePool(false);
    setSinglePoolPrizePool(0);
    setSinglePoolConfig(null);

    try {
      let currentUsername = '';
      try {
        const storedUser = localStorage.getItem('aureus_user');
        if (storedUser) {
          const parsed = JSON.parse(storedUser);
          currentUsername = parsed?.database_user?.username || parsed?.username || '';
        }
      } catch { /* ignore */ }

      const urlParams = new URLSearchParams({
        phaseId: String(phase.id),
        type: 'leaderboard',
        week: String(week),
      });
      if (currentUsername) urlParams.set('username', currentUsername);

      const response = await fetch(`/api/public/leaderboard?${urlParams.toString()}`);
      const data = await response.json();
      if (!response.ok || !data.success) {
        console.error('Error fetching leaderboard:', data.error);
        setLeaderboard([]);
        setCompetition(null);
        setSelectedCompetitionId(null);
        return;
      }
      applyLeaderboardPayload(data);
    } catch (error) {
      console.error('Error loading phase Diggers:', error);
      setLeaderboard([]);
      setCompetition(null);
      setSelectedCompetitionId(null);
    }
  };

  const fetchData = async () => {
    try {
      setError(null);

      const phasesResponse = await fetch('/api/public/leaderboard?type=phases');
      const phasesData = await phasesResponse.json();
      if (!phasesResponse.ok || !phasesData.success) {
        throw new Error(phasesData.error || 'Failed to fetch phases');
      }
      if (!phasesData.phases?.length) throw new Error('No share phases found');

      setAllPhases(phasesData.phases);
      // Diggers-active is Phase 10 (combined Aug–Sep window), not investment is_active
      const diggersActive =
        phasesData.phases.find((p: any) => isDiggersActivePhase(p)) ||
        phasesData.phases.find((p: any) => Number(p.id) === PHASE10_PHASE_ID) ||
        phasesData.phases.find((p: any) => p.is_active) ||
        phasesData.phases[0];
      await loadPhaseDiggers(diggersActive);
    } catch (error: any) {
      console.error('❌ Critical error fetching data:', error);
      setError(error.message || 'Failed to load data');
      setAllPhases([]);
      setSelectedPhase(null);
      setCompetition(null);
      setCompetitions([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePhaseChange = async (event: any) => {
    const selectedPhaseId = parseInt(event.target.value, 10);
    const phase = allPhases.find((p) => p.id === selectedPhaseId);
    if (!phase) {
      console.error('❌ Phase not found for ID:', selectedPhaseId);
      return;
    }
    await loadPhaseDiggers(phase, 'auto');
  };

  const handleWeekChange = async (week: string | number) => {
    if (!selectedPhase) return;
    setSelectedWeek(week);
    await loadPhaseDiggers(selectedPhase, week);
  };

  // Handle clicking on a leaderboard row to show/hide purchase details
  // v4.1.11: Uses opaque `ref` (e.g. 'r1') instead of raw user_id
  const handleRowClick = async (ref: string) => {
    // v4.3.60: Safety guard — reject undefined/null refs to prevent all rows expanding simultaneously
    if (!ref) return;

    // Toggle expansion
    if (expandedRef === ref) {
      setExpandedRef(null);
      return;
    }

    setExpandedRef(ref);

    // If we already have the details, don't fetch again
    if (referralDetails[ref]) {
      return;
    }

    try {
      // Fetch referral purchases from API using opaque ref
      const phaseId = selectedPhase?.id || competition?.phase_id;
      const competitionId = selectedCompetitionId || competition?.id;
      if (!phaseId || !competitionId) return;

      const urlParams = new URLSearchParams({
        phaseId: String(phaseId),
        ref: String(ref),
        competitionId: String(competitionId),
        week: String(selectedWeek || 'auto'),
      });
      const response = await fetch(`/api/public/leaderboard?type=referral-purchases&${urlParams.toString()}`);
      const data = await response.json();

      if (!response.ok || !data.success) {
        console.error('Error fetching referral purchases:', data.error);
        // v4.3.60: Store empty array on error so the UI doesn't show "Loading..." forever
        setReferralDetails((prev: any) => ({ ...prev, [ref]: [] }));
        return;
      }

      const purchases = data.purchases || [];
      const enrichedPurchases = buildClubPurchaseRows(purchases);

      // Store in state keyed by opaque ref
      setReferralDetails((prev: any) => ({
        ...prev,
        [ref]: enrichedPurchases
      }));

    } catch (error) {
      console.error('❌ Error fetching referral details:', error);
      // v4.3.60: Store empty array on error so the UI doesn't show "Loading..." forever
      setReferralDetails((prev: any) => ({ ...prev, [ref]: [] }));
    }
  };

  // Error state
  if (error) {
    return (
      <div className="gold-diggers-club">
        <div className="competition-header">
          <h2 className="competition-title">🏆 Gold Diggers Club</h2>
          <div className="error-state">
            <p>❌ Error: {error}</p>
            <button onClick={fetchData} className="retry-button">
              🔄 Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="gold-diggers-club">
        <div className="competition-header">
          <h2 className="competition-title">🏆 Gold Diggers Club</h2>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Show error state if no phase data available
  if (!selectedPhase || !allPhases.length) {
    return (
      <div className="gold-diggers-club">
        <div className="competition-header">
          <h2 className="competition-title">🏆 Gold Diggers Club</h2>
          <div className="error-state">
            <p>❌ Unable to load phase data from database</p>
            <p>Please check your connection and try again</p>
            <button onClick={fetchData} className="retry-button">
              🔄 Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Diggers prize pool: monthly 15% / weekly Mine Production+ 10%
  const weeklyMode = usesWeeklyDiggers(selectedPhase);
  const poolConfig = weeklyMode
    ? WEEKLY_DIGGERS_POOL_CONFIG
    : (singlePoolConfig?.prize_pool_percent != null ? singlePoolConfig : MONTHLY_DIGGERS_POOL_CONFIG);
  const poolRate = (poolConfig.prize_pool_percent ?? (weeklyMode ? 10 : 15)) / 100;
  const payoutSlots = poolConfig.payout_slots || (weeklyMode ? 100 : 50);
  const calculateDynamicPrizePool = () => {
    if (phaseRevenue > 0) return Math.round(phaseRevenue * poolRate);
    return 0;
  };

  const currentRevenue = phaseRevenue > 0 ? phaseRevenue : 0;
  const dynamicPrizePool = calculateDynamicPrizePool();
  const scoringStart = competition?.scoring_start || competition?.start_date;
  const scoringEnd = competition?.scoring_end || competition?.end_date;
  const isLiveCompetition = !!(competition && (competition.is_active || competition.status === 'active') &&
    (!scoringEnd || new Date(scoringEnd).getTime() >= Date.now()) &&
    (!scoringStart || new Date(scoringStart).getTime() <= Date.now()));

  // Get commission structure for selected phase
  const currentCommission = getCommissionStructure(selectedPhase);
  const prizePoolLabel = weeklyMode ? "This week's pool" : 'Prize pool';

  // Handle join competition click
  const handleJoinCompetition = () => {
    // Use the passed auth handler to maintain affiliate context
    if (onGoToAuth) {
      onGoToAuth('login');
    } else {
      // Fallback to direct redirect if auth handler not available
      window.location.href = '/login';
    }
  };

  return (
    <div className="gold-diggers-club">
      {/* Ultra Clean Hero */}
      <div className="ultra-clean-hero ultra-clean-hero-compact">
        <div className="ultra-hero-head">
          <div>
            <h1 className="ultra-hero-title">Gold Diggers Club</h1>
            <p className="ultra-hero-subtitle">Ranked by direct sales</p>
          </div>
          <button
            type="button"
            className="ultra-join-btn"
            onClick={howToEnter ? () => setHowToOpen(true) : handleJoinCompetition}
          >
            {howToEnter ? 'How To Enter' : 'Join free'}
          </button>
        </div>
      </div>

      <div className="simple-metrics-row simple-metrics-row-compact">
        <div className="simple-metric-item">
          <div className="simple-metric-value">${dynamicPrizePool.toLocaleString()}</div>
          <div className="simple-metric-label">{prizePoolLabel}</div>
        </div>
        <div className="simple-metric-item">
          <div className="simple-metric-value">{phaseActiveCompetitors}</div>
          <div className="simple-metric-label">Competitors</div>
        </div>
        <div className="simple-metric-item">
          <div className="simple-metric-value">${weeklyMode ? WEEKLY_DIGGERS_DISPLAY_SHARE_PRICE : (selectedPhase?.price_per_share || '5')}</div>
          <div className="simple-metric-label">Share price</div>
        </div>
        <div className="simple-metric-item highlight">
          <div className="simple-metric-value">{currentCommission.usdt}% + {currentCommission.shares}%</div>
          <div className="simple-metric-label">Commission</div>
        </div>
        {countdown && (
          <div className="simple-metric-item">
            <div className="simple-metric-value" style={{ color: countdown === 'Competition Ended' ? '#e74c3c' : '#c9a84c' }}>{countdown}</div>
            <div className="simple-metric-label">Time left</div>
          </div>
        )}
      </div>

      {/* Prize Pool Progress — Diggers competition window only */}
      {dynamicPrizePool > 0 && scoringStart && scoringEnd && isLiveCompetition && (() => {
        const start = new Date(scoringStart).getTime();
        const end = new Date(scoringEnd).getTime();
        const now = Date.now();
        const elapsed = Math.max(now - start, 1);
        const velocity = phaseRevenue / (elapsed / 86400000); // revenue per day
        const remainingDays = Math.max((end - now) / 86400000, 0);
        const estimatedFinalRevenue = phaseRevenue + (velocity * remainingDays);
        const estimatedFinalPool = Math.round(estimatedFinalRevenue * poolRate);
        const progressPct = estimatedFinalPool > 0 ? Math.min((dynamicPrizePool / estimatedFinalPool) * 100, 100) : 0;

        return (
          <div className="diggers-pool-progress">
            <div className="diggers-pool-progress-row">
              <span>Pool ${dynamicPrizePool.toLocaleString()}</span>
              <span>Est. ~${estimatedFinalPool.toLocaleString()} · ~${Math.round(velocity).toLocaleString()}/day</span>
            </div>
            <div className="diggers-pool-progress-track">
              <div className="diggers-pool-progress-fill" style={{ width: `${progressPct}%` }} />
            </div>
          </div>
        );
      })()}

      {/* Phase switch — Diggers window auto-resolves for the selected phase */}
      <div className="simple-phase-section">
        <div className="simple-phase-header">
          <select
            className="simple-phase-select"
            value={selectedPhase.id}
            onChange={handlePhaseChange}
          >
            {allPhases.map((phase: any) => (
              <option key={phase.id} value={phase.id}>
                {diggersPhaseLabel(phase)} - ${Number(usesWeeklyDiggers(phase) ? WEEKLY_DIGGERS_DISPLAY_SHARE_PRICE : phase.price_per_share).toFixed(2)}/share{isDiggersActivePhase(phase) ? ' (Active)' : ''}
              </option>
            ))}
          </select>
          {competition && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.85em', opacity: 0.75 }}>
              Diggers: {competition.name}
              {Array.isArray(competition.score_phase_ids) && competition.score_phase_ids.length > 1
                ? ' · combined Phase 9 + Phase 10 purchases in window'
                : null}
              {competition.start_date || competition.end_date ? (
                <>
                  {' · '}
                  {competition.start_date ? new Date(competition.start_date).toLocaleDateString() : '…'}
                  {' → '}
                  {competition.end_date ? new Date(competition.end_date).toLocaleDateString() : 'open'}
                </>
              ) : null}
              {isLiveCompetition ? ' · Live' : competition.status === 'ended' ? ' · Ended' : ''}
              {countdown ? ` · ${countdown}` : ''}
            </div>
          )}
          {weeklyMode && (
          <div className="diggers-week-tabs">
            {(weeks.length ? weeks : [1, 2, 3, 4].map((n) => ({ week: n, empty: false, isCurrent: false }))).map((w: any) => (
              <button
                key={w.week}
                type="button"
                className={`diggers-week-tab${Number(selectedWeek) === Number(w.week) ? ' active' : ''}`}
                disabled={!!w.empty}
                onClick={() => handleWeekChange(w.week)}
              >
                Week {w.week}{w.isCurrent ? ' · Live' : ''}
              </button>
            ))}
          </div>
          )}
          {weeklyMode && scoringStart && scoringEnd && (
            <div style={{ marginTop: '0.4rem', fontSize: '0.8em', opacity: 0.7 }}>
              Week {selectedWeek}: {new Date(scoringStart).toLocaleDateString()} → {new Date(scoringEnd).toLocaleDateString()} · 10% of this week&apos;s revenue
            </div>
          )}
        </div>

        <div className="simple-phase-stats">
          <div className="simple-stat">
            <span className="simple-stat-label">Competitors:</span>
            <span className="simple-stat-value">{phaseActiveCompetitors.toLocaleString()}</span>
          </div>
          <div className="simple-stat">
            <span className="simple-stat-label">Diggers Revenue:</span>
            <span className="simple-stat-value">${currentRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div className="simple-stat">
            <span className="simple-stat-label">Shares in Diggers Window:</span>
            <span className="simple-stat-value">
              {calculatedSharesSold?.toLocaleString(undefined, { maximumFractionDigits: 2 }) || '0'}
            </span>
          </div>
        </div>

        {/* Financial Breakdown */}
        <div className="financial-breakdown">
          <h4>Revenue split</h4>
          <div className={`financial-grid${weeklyMode ? ' financial-grid-six' : ''}`}>
            {(weeklyMode
              ? [
                  { label: '15% Commission', rate: 0.15, valueClass: 'teal' },
                  { label: '10% Gold Diggers', rate: 0.10, valueClass: 'gold', highlight: true },
                  { label: '10% Daily Performance Pool', rate: 0.10, valueClass: 'teal' },
                  { label: '35% Aureus', rate: 0.35, valueClass: 'rose' },
                  { label: '10% UHHF', rate: 0.10, valueClass: 'sky' },
                  { label: '20% SUN', rate: 0.20, valueClass: 'gold' },
                ]
              : [
                  { label: '15% Commission', rate: 0.15, valueClass: 'teal' },
                  { label: `${Math.round(poolRate * 100)}% Gold Diggers`, rate: poolRate, valueClass: 'gold', highlight: true },
                  { label: '35% Aureus', rate: 0.35, valueClass: 'rose' },
                  { label: '10% Reserve', rate: 0.10, valueClass: 'sky' },
                ]
            ).map((slice) => (
              <div key={slice.label} className={`financial-cell${slice.highlight ? ' gold-highlight' : ''}`}>
                <div className={`financial-cell-label${slice.highlight ? ' gold' : ''}`}>{slice.label}</div>
                <div className={`financial-cell-value ${slice.valueClass}`}>${(currentRevenue * slice.rate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              </div>
            ))}
          </div>
          {!weeklyMode && (
            <div className="financial-footer">
              {(() => {
                const accountedRate = 0.15 + poolRate + 0.35 + 0.10;
                return (
                  <>
                    Accounted: {(accountedRate * 100).toFixed(0)}% of ${currentRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} = ${(currentRevenue * accountedRate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} | Remaining {((1 - accountedRate) * 100).toFixed(0)}%: ${(currentRevenue * (1 - accountedRate)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Prize Tiers Section */}
      <div className="prize-tiers">
        {isSinglePool && (selectedPhase?.phase_number ?? competition?.phase?.phase_number ?? 0) >= 5 ? (
          /* Phase 5+ monthly Top 50 (15%) or Mine Production+ weekly Top 100 (10%) */
          (() => {
            const pool = singlePoolPrizePool || dynamicPrizePool;
            const pcts = weeklyMode
              ? WEEKLY_DIGGERS_POOL_CONFIG.payout_percentages
              : (singlePoolConfig?.payout_percentages || MONTHLY_DIGGERS_POOL_CONFIG.payout_percentages);
            const weeklyTiers = [
              { key: 1, label: '1ST PLACE', emoji: '🥇', className: 'champion' },
              { key: 2, label: '2ND PLACE', emoji: '🥈', className: 'runner-up' },
              { key: 3, label: '3RD PLACE', emoji: '🥉', className: 'bronze' },
              { key: '4-10', label: '4TH–10TH', emoji: '🏆', className: 'elite', each: true },
              { key: '11-20', label: '11TH–20TH', emoji: '⭐', className: 'elite', each: true },
              { key: '21-40', label: '21ST–40TH', emoji: '💫', className: 'elite', each: true },
              { key: '41-60', label: '41ST–60TH', emoji: '✨', className: 'elite', each: true },
              { key: '61-100', label: '61ST–100TH', emoji: '✨', className: 'elite', each: true },
            ];
            const monthlyTiers = [
              { key: 1, label: '1ST PLACE', emoji: '🥇', className: 'champion' },
              { key: 2, label: '2ND PLACE', emoji: '🥈', className: 'runner-up' },
              { key: 3, label: '3RD PLACE', emoji: '🥉', className: 'bronze' },
              { key: '4-10', label: '4TH–10TH', emoji: '🏆', className: 'elite', each: true },
              { key: '11-20', label: '11TH–20TH', emoji: '⭐', className: 'elite', each: true },
              { key: '21-30', label: '21ST–30TH', emoji: '💫', className: 'elite', each: true },
              { key: '31-50', label: '31ST–50TH', emoji: '✨', className: 'elite', each: true },
            ];
            const tiers = weeklyMode ? weeklyTiers : monthlyTiers;
            const prizeAmounts = allocateClubPrizeAmounts(pool, pcts, payoutSlots);
            const amountForTier = (key: number | string) => {
              const [lo, hi] = String(key).includes('-')
                ? String(key).split('-').map(Number)
                : [Number(key), Number(key)];
              const band = prizeAmounts.filter((row) => row.rank >= lo && row.rank <= hi).map((row) => row.amount);
              if (!band.length) return '$0';
              const min = Math.min(...band);
              const max = Math.max(...band);
              const each = lo !== hi;
              if (min === max) return `$${min.toLocaleString()}`;
              return `$${min.toLocaleString()}–$${max.toLocaleString()}`;
            };
            return (
            <>
              <h3>Top {payoutSlots} · ${pool.toLocaleString()} pool</h3>
              <div className="tiers-grid tiers-grid-compact">
                {tiers.map((tier) => {
                  const pct = pcts[tier.key] ?? 0;
                  return (
                    <div key={String(tier.key)} className={`tier-card tier-card-compact ${tier.className}`}>
                      <div className="tier-name">{tier.label}</div>
                      <div className="tier-prize">{amountForTier(tier.key)}</div>
                      <div className="tier-rank">{pct}%{tier.each ? ' each' : ''}</div>
                    </div>
                  );
                })}
              </div>
            </>
            );
          })()
        ) : (
          /* Phase 4 and earlier: Top 10 Prize Tiers */
          <>
            <h3>💰 Top 10 Prize Tiers</h3>
            <p>The more you refer, the bigger your rewards! Top 10 affiliates win from the prize pool.</p>
            <div className="tiers-grid">
              {(() => {
                const pool = dynamicPrizePool;
                const p1 = prizeTierDisplay?.find((t: any) => t.rankStart === 1)?.percent ?? 40;
                const p2 = prizeTierDisplay?.find((t: any) => t.rankStart === 2)?.percent ?? 20;
                const p3 = prizeTierDisplay?.find((t: any) => t.rankStart === 3)?.percent ?? 12;
                const p4 = prizeTierDisplay?.find((t: any) => t.rankStart === 4)?.percent ?? 4;
                return (
                  <>
                    <div className="tier-card champion">
                      <div className="tier-emoji">{prizeTierDisplay?.find((t: any) => t.rankStart === 1)?.emoji || '🥇'}</div>
                      <div className="tier-name">CHAMPION</div>
                      <div className="tier-prize">${Math.round(pool * (p1 / 100)).toLocaleString()}</div>
                      <div className="tier-rank">1st Place — {p1}%</div>
                      <div className="tier-description">Ultimate Winner</div>
                    </div>
                    <div className="tier-card runner-up">
                      <div className="tier-emoji">{prizeTierDisplay?.find((t: any) => t.rankStart === 2)?.emoji || '🥈'}</div>
                      <div className="tier-name">RUNNER-UP</div>
                      <div className="tier-prize">${Math.round(pool * (p2 / 100)).toLocaleString()}</div>
                      <div className="tier-rank">2nd Place — {p2}%</div>
                      <div className="tier-description">Silver Medal</div>
                    </div>
                    <div className="tier-card bronze">
                      <div className="tier-emoji">{prizeTierDisplay?.find((t: any) => t.rankStart === 3)?.emoji || '🥉'}</div>
                      <div className="tier-name">BRONZE</div>
                      <div className="tier-prize">${Math.round(pool * (p3 / 100)).toLocaleString()}</div>
                      <div className="tier-rank">3rd Place — {p3}%</div>
                      <div className="tier-description">Bronze Medal</div>
                    </div>
                    <div className="tier-card elite">
                      <div className="tier-emoji">{prizeTierDisplay?.find((t: any) => t.rankStart === 4)?.emoji || '🏆'}</div>
                      <div className="tier-name">ELITE</div>
                      <div className="tier-prize">${Math.round(pool * (p4 / 100)).toLocaleString()}</div>
                      <div className="tier-rank">4th-10th — {p4}% each</div>
                      <div className="tier-description">Each Winner</div>
                    </div>
                  </>
                );
              })()}
            </div>
          </>
        )}
      </div>

      {/* How Competition Works - Clear Explanation */}
      <div className="competition-rules-section">
        <div className="rules-header">
          <h3>Competition Rules & Ranking System</h3>
          <p className="rules-subtitle">Transparent, fair, and based on direct sales performance only</p>
        </div>

        {/* How Rankings Are Calculated */}
        <div className="calculation-explanation calculation-compact">
          <h4>How ranking is calculated</h4>
          <div className="calculation-grid">
            <div className="calc-card counted">
              <h5>Counts</h5>
              <p>Approved purchases by your direct referrals</p>
            </div>
            <div className="calc-card not-counted">
              <h5>Does not count</h5>
              <p>Indirect referrals and your own purchases</p>
            </div>
          </div>
        </div>

        {/* Step-by-Step Guide */}
        <div className="how-it-works how-it-works-compact">
          <h4>How to Win</h4>
          <div className="steps-grid steps-grid-compact">
            <div className="step-card step-card-compact">
              <div className="step-number">1</div>
              <h5>Join free</h5>
              <p>No cost to join</p>
            </div>
            <div className="step-card step-card-compact">
              <div className="step-number">2</div>
              <h5>Get your link</h5>
              <p>Share it with buyers</p>
            </div>
            <div className="step-card step-card-compact">
              <div className="step-number">3</div>
              <h5>Direct sales</h5>
              <p>Only your referrals count</p>
            </div>
            <div className="step-card step-card-compact">
              <div className="step-number">4</div>
              <h5>Earn and compete</h5>
              <p>{currentCommission.usdt}% USDT + {currentCommission.shares}% shares</p>
            </div>
          </div>
        </div>

        {/* v4.1.9: Phase 5+ Qualification Requirements section removed (Top 40 reverted to Top 10) */}

        {/* Example Scenario */}
        <div className="example-scenario rank-story">
          <h4>How ranking works</h4>
          <div className="rank-flow">
            <div className="rank-actor">
              <span className="rank-face mike" aria-hidden="true"><span className="rank-body" /></span>
              <span className="rank-name">Mike</span>
              <span className="rank-amt">$20,000</span>
            </div>
            <div className="rank-link blocked" aria-hidden="true">
              <span className="rank-coin bad" />
              <span className="rank-x">X</span>
            </div>
            <div className="rank-actor">
              <span className="rank-face john" aria-hidden="true"><span className="rank-body" /></span>
              <span className="rank-name">John</span>
              <span className="rank-amt good">$5,000</span>
            </div>
            <div className="rank-link counts" aria-hidden="true">
              <span className="rank-coin" />
            </div>
            <div className="rank-actor you">
              <span className="rank-face you" aria-hidden="true"><span className="rank-body" /></span>
              <span className="rank-name">You</span>
              <span className="rank-amt score">$15,000</span>
            </div>
            <div className="rank-link counts reverse" aria-hidden="true">
              <span className="rank-coin late" />
            </div>
            <div className="rank-actor">
              <span className="rank-face sarah" aria-hidden="true"><span className="rank-body" /></span>
              <span className="rank-name">Sarah</span>
              <span className="rank-amt good">$10,000</span>
            </div>
          </div>
          <p className="rank-caption">John and Sarah bought with your link, so $5,000 + $10,000 counts. Mike bought with John's link. The X stops his $20,000. Your rank is $15,000.</p>
        </div>
      </div>



      {/* ===== Competition Leaderboard ===== */}
      <div className="leaderboard-section">
          <div className="leaderboard-header">
            <div>
              <h3 className="leaderboard-title">🏆 {competition?.name || 'Competition Leaderboard'}</h3>
              <p className="leaderboard-subtitle">
                {isSinglePool
                  ? `Top ${payoutSlots} affiliates ranked by sales volume win from the $${(singlePoolPrizePool || dynamicPrizePool).toLocaleString()} prize pool!`
                  : `Top 10 affiliates win from the $${dynamicPrizePool.toLocaleString()} prize pool!`
                }
                {weeklyMode && competition?.scoring_start && competition?.scoring_end ? (
                  <span style={{ display: 'block', fontSize: '0.85em', opacity: 0.7, marginTop: '2px' }}>
                    {`Week ${selectedWeek} · `}
                    {new Date(competition.scoring_start).toLocaleDateString()} — {new Date(competition.scoring_end).toLocaleDateString()}
                  </span>
                ) : competition?.start_date && competition?.end_date && (
                  <span style={{ display: 'block', fontSize: '0.85em', opacity: 0.7, marginTop: '2px' }}>
                    {new Date(competition.start_date).toLocaleDateString()} — {new Date(competition.end_date).toLocaleDateString()}
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* v4.1.11: "Your Position" banner when user is ranked outside visible Top 10 */}
          {yourPosition && yourPosition.rank > 10 && (
            <div className="your-position-banner" style={{
              background: 'linear-gradient(135deg, #1a3a2a 0%, #0d1f17 100%)',
              border: '1px solid #c9a84c',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              color: '#c9a84c'
            }}>
              <span style={{ fontSize: '1.2em' }}>📍</span>
              <span>You're currently ranked <strong>#{yourPosition.rank}</strong> with ${yourPosition.totalVolume?.toLocaleString()} volume — keep going!</span>
            </div>
          )}

          {/* v4.1.11: Share Your Rank button */}
          {yourPosition && (
            <div style={{ textAlign: 'right', marginBottom: '8px' }}>
              <button
                onClick={async () => {
                  const text = `🏆 I'm ranked #${yourPosition.rank} in the Gold Diggers Club competition! Prize pool: $${dynamicPrizePool.toLocaleString()}. Join now at aureusalliance.com`;
                  if (navigator.share) {
                    try { await navigator.share({ title: 'Gold Diggers Club', text }); } catch { /* cancelled */ }
                  } else {
                    try { await navigator.clipboard.writeText(text); alert('Rank copied to clipboard!'); } catch { /* fallback */ }
                  }
                }}
                style={{
                  background: 'transparent',
                  border: '1px solid #c9a84c',
                  color: '#c9a84c',
                  padding: '6px 14px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.85em'
                }}
              >
                📤 Share Your Rank
              </button>
            </div>
          )}

          {leaderboard.length > 0 ? (
            <div className="leaderboard-table">
              <div className="leaderboard-header">
                <span>Rank</span>
                <span>Affiliate</span>
                <span>{isSinglePool ? 'Buyers' : 'Referrals'}</span>
                <span>Volume</span>
                <span>{isSinglePool ? 'Prize' : 'Prize Tier'}</span>
                <span>Paid</span>
              </div>
              {(showAllLeaderboard ? leaderboard : leaderboard.slice(0, 10)).map((entry: any, index: number) => {
                const isYou = yourPosition && entry.ref === yourPosition.ref;
                return (
                <React.Fragment key={entry.ref || index}>
                  <div
                    className={`leaderboard-row ${entry.isPlaceholder ? 'placeholder-row' : index < 3 ? 'top-three' : index < 10 ? 'prize-tier' : 'no-prize'} ${!entry.isPlaceholder ? 'clickable' : ''} ${expandedRef === entry.ref ? 'expanded' : ''} ${isYou ? 'your-row' : ''}`}
                    onClick={() => !entry.isPlaceholder && handleRowClick(entry.ref)}
                    style={{
                      cursor: !entry.isPlaceholder ? 'pointer' : 'default',
                      ...(isYou ? { borderLeft: '3px solid #c9a84c', background: 'rgba(201, 168, 76, 0.08)' } : {})
                    }}
                  >
                    {entry.isPlaceholder ? (
                      <>
                        <span className="rank">⏳</span>
                        <span className="username">{entry.username}</span>
                        <span className="referrals">-</span>
                        <span className="volume">-</span>
                        <span className="prize">{entry.prize_tier}</span>
                        <span className="pay-status">—</span>
                      </>
                    ) : (
                      <>
                        <span className="rank">
                          {entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : entry.rank === 3 ? '🥉' : `#${entry.rank}`}
                          {entry.rankChange != null && entry.rankChange !== 0 && (
                            <span style={{ marginLeft: '4px', fontSize: '0.7em', color: entry.rankChange > 0 ? '#2ecc71' : '#e74c3c' }}>
                              {entry.rankChange > 0 ? `▲${entry.rankChange}` : `▼${Math.abs(entry.rankChange)}`}
                            </span>
                          )}
                        </span>
                        <span className="username">
                          {entry.username}
                          {isYou && <span style={{ marginLeft: '6px', background: '#c9a84c', color: '#000', borderRadius: '4px', padding: '1px 6px', fontSize: '0.7em', fontWeight: 'bold' }}>YOU</span>}
                          <span className="expand-icon">{expandedRef === entry.ref ? '▼' : '▶'}</span>
                        </span>
                        <span className="referrals">{entry.total_referrals}</span>
                        <span className="volume">${entry.total_volume?.toLocaleString()}</span>
                        <span className="prize">{isSinglePool ? (entry.prize_bonus > 0 ? (
                          <span className="prize-breakdown">
                            <span className="prize-part">${Number(entry.prize_rank_amount).toLocaleString()} ({entry.prize_percent}%)</span>
                            <span className="prize-part">+ ${Number(entry.prize_bonus).toLocaleString()} share</span>
                            <span className="prize-total">= ${Number(entry.prize_rank_amount + entry.prize_bonus).toLocaleString()}</span>
                          </span>
                        ) : `${entry.prize_tier} (${entry.prize_percent}%)`) : (index < 10 ? entry.prize_tier : '-')}</span>
                        <span className={`pay-status ${entry.prize_paid ? 'is-paid' : 'is-unpaid'}`}>{entry.prize_paid ? 'Paid' : 'Not paid yet'}</span>
                      </>
                    )}
                  </div>

                  {/* Expanded purchase details */}
                  {!entry.isPlaceholder && expandedRef === entry.ref && referralDetails[entry.ref] && (
                    <div className="purchase-details-container">
                      <div className="purchase-details-header">
                        <h4>Direct purchases</h4>
                      </div>
                      <div className="purchase-details-table">
                        <div className="purchase-details-header-row">
                          <span>Buyer</span>
                          <span>Amount</span>
                          <span>Cost/Share</span>
                          <span>Shares</span>
                          <span>Date</span>
                        </div>
                        {referralDetails[entry.ref].map((purchase: any, idx: number) => (
                          <div key={idx} className="purchase-details-row">
                            <span className="buyer-name">{purchase.buyerLabel}</span>
                            <span className="purchase-amount">${formatClubNumber(purchase.amount, 2)}</span>
                            <span className="purchase-cost-per-share">${formatClubNumber(Number(selectedPhase.price_per_share) || 0, 2)}</span>
                            <span className="purchase-shares">{formatClubNumber(purchase.paidShares, 4)}</span>
                            <span className="purchase-date">{purchase.date}</span>
                          </div>
                        ))}
                        <div className="purchase-details-total">
                          <span>Total:</span>
                          <span>${formatClubNumber(referralDetails[entry.ref].reduce((sum: number, p: any) => sum + p.amount, 0), 2)}</span>
                          <span></span>
                          <span className="purchase-shares">
                            {formatClubNumber(referralDetails[entry.ref].reduce((sum: number, p: any) => sum + p.paidShares, 0), 4)} shares
                          </span>
                          <span>{referralDetails[entry.ref].length} purchases</span>
                        </div>
                      </div>
                    </div>
                  )}
                  {!entry.isPlaceholder && expandedRef === entry.ref && !referralDetails[entry.ref] && (
                    <div className="purchase-details-container">
                      <p style={{ textAlign: 'center', padding: '12px', color: '#ffffff' }}>Loading purchases...</p>
                    </div>
                  )}
                </React.Fragment>
                );
              })}
              {/* v4.1.11: Show All / Show Top 10 toggle */}
              {leaderboard.length > 10 && (
                <div style={{ textAlign: 'center', padding: '12px 0' }}>
                  <button
                    onClick={() => setShowAllLeaderboard(!showAllLeaderboard)}
                    style={{
                      background: 'transparent',
                      border: '1px solid #c9a84c',
                      color: '#c9a84c',
                      padding: '8px 20px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.9em',
                      fontWeight: 'bold'
                    }}
                  >
                    {showAllLeaderboard ? `▲ Show Top ${isSinglePool ? payoutSlots : '10'} Only` : `▼ Show All ${leaderboard.length} Competitors`}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="leaderboard-empty">
              <p>🚀 Be the first to join and claim the top spot!</p>
              <p>Competition is just getting started - perfect time to join!</p>
            </div>
          )}
      </div>

      {howToEnter && howToOpen && (
        <HowToEnterDialog
          onClose={() => setHowToOpen(false)}
          weeklyMode={weeklyMode}
          isSinglePool={isSinglePool}
          payoutSlots={payoutSlots}
          poolPercent={Math.round(poolRate * 100)}
          commissionUsdt={currentCommission.usdt}
          commissionShares={currentCommission.shares}
          windowLabel={
            scoringStart && scoringEnd
              ? `${new Date(scoringStart).toLocaleDateString()} – ${new Date(scoringEnd).toLocaleDateString()}`
              : 'the current scoring window'
          }
          weekNumber={weeklyMode ? selectedWeek : null}
          prizeLines={
            weeklyMode
              ? prizeLinesFromConfig(WEEKLY_DIGGERS_POOL_CONFIG.payout_percentages, WEEKLY_PRIZE_ORDER)
              : isSinglePool
                ? prizeLinesFromConfig(
                    singlePoolConfig?.payout_percentages || MONTHLY_DIGGERS_POOL_CONFIG.payout_percentages,
                    MONTHLY_PRIZE_ORDER
                  )
                : [
                    { place: '1st place', share: `${prizeTierDisplay?.find((t: any) => t.rankStart === 1)?.percent ?? 40}%` },
                    { place: '2nd place', share: `${prizeTierDisplay?.find((t: any) => t.rankStart === 2)?.percent ?? 20}%` },
                    { place: '3rd place', share: `${prizeTierDisplay?.find((t: any) => t.rankStart === 3)?.percent ?? 12}%` },
                    { place: '4th–10th', share: `${prizeTierDisplay?.find((t: any) => t.rankStart === 4)?.percent ?? 4}% each` },
                  ]
          }
        />
      )}

    </div>
  );
};

function HowToEnterDialog({
  onClose,
  weeklyMode,
  isSinglePool,
  payoutSlots,
  poolPercent,
  commissionUsdt,
  commissionShares,
  windowLabel,
  weekNumber,
  prizeLines,
}: {
  onClose: () => void;
  weeklyMode: boolean;
  isSinglePool: boolean;
  payoutSlots: number;
  poolPercent: number;
  commissionUsdt: number;
  commissionShares: number;
  windowLabel: string;
  weekNumber: string | number | null;
  prizeLines: { place: string; share: string }[];
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const commissionText = commissionShares > 0
    ? `${commissionUsdt}% in USDT and ${commissionShares}% in shares`
    : `${commissionUsdt}% in USDT`;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    dialogRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const scoreWindow = weeklyMode
    ? `Mine Production and later phases are scored one week at a time. ${weekNumber != null && weekNumber !== 'auto' ? `Week ${weekNumber} is ${windowLabel}.` : `The week on screen is ${windowLabel}.`} Only direct sales inside that week count toward this rank. The next week starts again from zero. The prize pool is ${poolPercent}% of the cash sales counted in the week. The top ${payoutSlots} scores share that pool.`
    : `This phase is scored across one window: ${windowLabel}. The prize pool is ${poolPercent}% of the cash sales counted in that window. The top ${payoutSlots} scores share that pool.`;

  return createPortal(
    <div className="diggers-howto-backdrop" onClick={onClose}>
      <div
        ref={dialogRef}
        className="diggers-howto-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="diggers-howto-title"
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="diggers-howto-head">
          <h2 id="diggers-howto-title">How to enter</h2>
          <button type="button" className="diggers-howto-close" onClick={onClose} aria-label="Close">
            Close
          </button>
        </div>
        <p className="diggers-howto-lead">
          There is no entry fee and no separate signup. An affiliate account is the entry. Your place is decided only by share sales your direct referrals complete inside the scoring window.
        </p>

        <h3>How to get on the board</h3>
        <ol className="diggers-howto-steps">
          <li>Open Marketing Tools and copy your referral link.</li>
          <li>Share that link. A person who registers with it is your direct referral.</li>
          <li>When that person buys shares, and the purchase is a completed cash sale inside the window, those dollars are added to your score.</li>
          <li>The leaderboard sorts by that score. The highest score is 1st place.</li>
        </ol>

        <h3>This competition window</h3>
        <p>{scoreWindow}</p>
        <p>
          {weeklyMode
            ? 'A sale is placed in the week that contains the time the buyer submitted the payment.'
            : 'A sale counts when it falls inside the window dates above.'}
          {' '}Your own purchases never count for you. They can count for the person whose link you used.
        </p>

        <h3>What moves your rank</h3>
        <div className="diggers-howto-split">
          <div>
            <h4>Counts</h4>
            <ul>
              <li>Completed cash share purchases by your direct referrals.</li>
              <li>Every such purchase in the window is added. Repeat buyers add more dollars.</li>
              <li>The score is those sale dollars. It is rounded to the nearest dollar before places are set.</li>
            </ul>
          </div>
          <div>
            <h4>Does not count</h4>
            <ul>
              <li>Purchases you make yourself.</li>
              <li>Sales made by someone your referral brought in. Only your own link scores for you.</li>
              <li>Gifts, shares paid from commission, transfers, consolidations, and pool claims.</li>
            </ul>
          </div>
        </div>
        <p>If two scores match after rounding, the affiliate with more direct buyers ranks higher.</p>

        <h3>How you earn while you compete</h3>
        <p>
          Each qualifying direct sale also pays your referral commission, shown on this screen as {commissionText}. That commission is paid on the sale. It is not the competition prize, it is not taken from the prize pool, and it does not set your rank. Rank follows the sale dollars.
        </p>

        <h3>How you win</h3>
        <p>
          When the {weeklyMode ? 'week' : 'window'} closes, places are taken from the pool in this order. Prizes are USDT, credited to the commission balance after the results are awarded.
        </p>
        <ul className="diggers-howto-prizes">
          {prizeLines.map((line) => (
            <li key={line.place}>
              <span>{line.place}</span>
              <span>{line.share}</span>
            </li>
          ))}
        </ul>
        {(weeklyMode || isSinglePool) && (
          <p>
            A place with nobody in it pays nothing. If fewer than {payoutSlots} people have a score, the dollars set aside for the empty places are split across the people who did qualify, on top of their place prize. The prizes still add up to the pool.
          </p>
        )}

        <h3>A ranking example</h3>
        <p>
          John buys $5,000 with your link, and Sarah buys $10,000 with your link. Both count, so your score is $15,000. Mike buys $20,000 with John’s link. That sale counts for John, not for you. Your own purchase, if you made one, would count for your sponsor, not for your rank.
        </p>
      </div>
    </div>,
    document.body
  );
}

export default GoldDiggersClub;
